import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { orderAPI } from '../services/api';
import FilterPanel from '../components/FilterPanel';
import OrderDetail from '../components/OrderDetail';
import { getOrderColors, PRIORITY_COLORS, STATUS_COLORS } from '../utils/statusColors';
import SearchBar from '../components/SearchBar';

const TableView = () => {
  const { selectedShop } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State Initialization - Pull URL params (from Dashboard routing) into local state if they exist
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
    item_type: searchParams.get('item_type') || '',
    is_overdue: searchParams.get('overdue') === 'true' ? 'true' : '',
    is_active: searchParams.get('active') === 'true' ? 'true' : '',
    page: parseInt(searchParams.get('page')) || 1,
    limit: 50
  });

  const [data, setData] = useState({ orders: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Selected Order for the Detail Modal
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Sync URL params when local filter state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.item_type) params.set('item_type', filters.item_type);
    if (filters.is_overdue) params.set('overdue', 'true');
    if (filters.is_active) params.set('active', 'true');
    params.set('page', filters.page);
    setSearchParams(params);
  }, [filters, setSearchParams]);

 

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // GET /orders/filter is needed for both overdue_only and active_only —
      // it returns a plain array (no pagination), unlike GET /orders/.
      if (filters.is_overdue === 'true' || filters.is_active === 'true') {
        const filterPayload = { shop: selectedShop };
        if (filters.is_overdue === 'true') filterPayload.overdue_only = true;
        if (filters.is_active === 'true') filterPayload.active_only = true;
        if (filters.status) filterPayload.status = filters.status;
        if (filters.priority) filterPayload.priority = filters.priority;
        if (filters.item_type) filterPayload.item_type = filters.item_type;

        const response = await orderAPI.filterOrders(filterPayload);
        const filteredOrders = Array.isArray(response.data) ? response.data : [];

        setData({ orders: filteredOrders, total: filteredOrders.length });
        return;
      }

      // Normal paginated path — unchanged
      const payload = {
        shop: selectedShop,
        page: filters.page,
        limit: filters.limit
      };

      if (filters.status) payload.status = filters.status;
      if (filters.priority) payload.priority = filters.priority;
      if (filters.item_type) payload.item_type = filters.item_type;

      const response = await orderAPI.list(payload);
      setData({
        orders: response.data?.orders || [],
        total: response.data?.total || 0
      });

    } catch (err) {
      setError('Failed to load table data.');
      setData({ orders: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedShop, filters]);

  
  // Re-fetch on filter or shop change
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleClearFilters = () => {
    setFilters({ status: '', priority: '', item_type: '', is_overdue: '', is_active: '', page: 1, limit: 50 });
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const totalPages = Math.ceil(data.total / filters.limit);
  const today = new Date().toLocaleDateString('en-CA');

  return (
    <div className="flex flex-col h-full max-w-7xl mx-auto pb-10">
      
      <div className="flex justify-between items-end mb-4">
        <h2 className="text-2xl font-bold text-gray-800 tracking-tight">Orders List</h2>
        <span className="text-sm font-medium text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-200">
          Total: {data.total}
        </span>
      </div>

      <SearchBar />
      <FilterPanel filters={filters} setFilters={setFilters} onClear={handleClearFilters} />

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4 font-medium border border-red-200">
          {error}
        </div>
      )}

      {/* Main Table Container */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden flex-1">
        <div className="overflow-x-auto min-h-[400px]">
          <table className="min-w-full divide-y divide-gray-200">
            
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Order ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Customer</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Target Date</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            
            <tbody className="bg-white divide-y divide-gray-200 relative">
              {loading && data.orders.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-20 text-center">
                    <div className="flex justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-600 rounded-full border-t-transparent"></div></div>
                  </td>
                </tr>
              ) : data.orders.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-20 text-center text-gray-500 font-medium">
                    No orders match your current filters.
                  </td>
                </tr>
              ) : (
                data.orders.map((order) => {
                  
                  // Row Level Styling Logic
                  let rowClasses = "cursor-pointer transition-colors hover:shadow-inner";
                  if (order.status !== 'Delivered' && order.status !== 'Cancelled') {
                    if (order.is_overdue || order.target_date < today) {
                      rowClasses += " bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500";
                    } else if (order.target_date === today) {
                      rowClasses += " bg-yellow-50 hover:bg-yellow-100 border-l-4 border-l-yellow-500";
                    } else {
                      rowClasses += " hover:bg-gray-50 border-l-4 border-l-transparent";
                    }
                  } else {
                    rowClasses += " hover:bg-gray-50 border-l-4 border-l-transparent";
                  }

                  const statusColors = STATUS_COLORS[order.status] || STATUS_COLORS['Pending'];
                  const targetFormatted = new Date(order.target_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

                  return (
                    <tr key={order.order_id} onClick={() => setSelectedOrder(order)} className={rowClasses}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {order.order_id}
                        {order.priority === 'Urgent' && <span className="ml-2 text-[10px] text-red-600 font-black">URGENT</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                        <div className="text-sm text-gray-500">{order.phone_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-bold rounded-full border ${statusColors.bg} ${statusColors.text} ${statusColors.border}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                        {targetFormatted}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-black text-gray-900">
                        {order.total_amount !== null ? `₹${order.total_amount}` : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {data.total > 0 && filters.is_overdue !== 'true' && filters.is_active !== 'true' && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((filters.page - 1) * filters.limit) + 1}</span> to <span className="font-medium">{Math.min(filters.page * filters.limit, data.total)}</span> of <span className="font-medium">{data.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button 
                    onClick={() => handlePageChange(filters.page - 1)} 
                    disabled={filters.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Previous
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {filters.page} of {totalPages}
                  </span>
                  <button 
                    onClick={() => handlePageChange(filters.page + 1)} 
                    disabled={filters.page >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
            
            {/* Mobile Pagination Controls */}
            <div className="flex-1 flex justify-between sm:hidden">
              <button 
                onClick={() => handlePageChange(filters.page - 1)} 
                disabled={filters.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100"
              >
                Previous
              </button>
              <button 
                onClick={() => handlePageChange(filters.page + 1)} 
                disabled={filters.page >= totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Selected Order Detail Modal Wrapper */}
      {selectedOrder && (
        <OrderDetail 
          order={selectedOrder} 
          onClose={() => setSelectedOrder(null)} 
          onUpdate={() => { fetchOrders(); setSelectedOrder(null); }} 
        />
      )}

    </div>
  );
};

export default TableView;